// src/components/pages/CustomFunnelSalesPage.tsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  //Star as IconStar,
  ChevronDown,
  ChevronUp,
  Zap,
  Calendar,
} from "lucide-react";
import data from "../../data/customFunnelData.json";

type Feature = (typeof data.features)[number];
// type Testimonial = (typeof data.testimonials)[number];
type PlatImg = (typeof data.platformImages)[number];
type FaqItem = (typeof data.faqs.questions)[number];

const currencySymbol = data.pricing.symbol ?? "₦";

function fmtMoney(value?: number) {
  if (value === undefined || value === null) return "-";
  return `${currencySymbol}${(value / 100).toLocaleString("en-NG", {
    minimumFractionDigits: 0,
  })}`;
}

const CheckCircle: React.FC = () => (
  <div className="flex-shrink-0 w-5 h-5 rounded-lg bg-green-100 flex items-center justify-center mt-0.5">
    <span className="text-green-600 text-xs font-bold">✓</span>
  </div>
);

/**
 * Facebook Pixel:
 * Provide a PIXEL_ID here (falls back to a reasonable default if not set).
 * You can set REACT_APP_FB_PIXEL_ID in your environment to override.
 */
const PIXEL_ID = "1196072802018516";

const CustomFunnelSalesPage: React.FC = () => {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  useEffect(() => {
    // Use trackSingle with explicit pixel id to avoid ambiguity and to match fbq typings
    try {
      window.fbq?.("trackSingle", PIXEL_ID, "ViewContent", {
        content_name: "Launch Kit Naija Sales Page",
        currency: "NGN",
        value: 197893,
      });
    } catch (e) {
      // silence any fbq errors (pixel not loaded, etc.)
      // console.debug("fbq track error:", e);
    }
  }, []);

  const split1 = data.pricing.splitFirst ?? 0;
  const split2 = data.pricing.splitSecond ?? 0;
  const split3 = data.pricing.splitThird ?? 0;
  const splitTotal = split1 + split2 + split3;

  const toggleFaq = (i: number) =>
    setExpandedFaq((cur) => (cur === i ? null : i));

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: data.design?.pageBackground ?? "#FFFFFF" }}
    >
      {/* ── Header ── */}
      <header className="bg-gradient-to-r from-brandNavy to-blue-900 text-white sticky top-0 z-50 shadow-lg">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <Link
            to={data.routes.home}
            className="text-xl font-extrabold tracking-tight"
          >
            {data.meta.brandName}
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <a
              href="#features"
              className="text-blue-100 hover:text-white text-sm transition-colors"
            >
              Features
            </a>
            <a
              href="#dashboard"
              className="text-blue-100 hover:text-white text-sm transition-colors"
            >
              Platform
            </a>

            <a
              href="#pricing"
              className="text-blue-100 hover:text-white text-sm transition-colors"
            >
              Pricing
            </a>
            <a
              href="#faqs"
              className="text-blue-100 hover:text-white text-sm transition-colors"
            >
              FAQs
            </a>
            <Link
              to={data.routes.payment}
              className="ml-2 bg-brandRed hover:bg-brandRedHover text-white font-bold text-sm px-5 py-2 rounded-lg shadow transition-colors"
            >
              Claim Your Slot →
            </Link>
          </nav>
        </div>
      </header>

      {/* ── Main ── */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* ════════════════════════════════════════
              HERO
          ════════════════════════════════════════ */}
          <section className="px-6 sm:px-12 py-12 text-center">
            {data.hero.preHeadline && (
              <div className="inline-block mb-4 px-4 py-1.5 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm text-orange-700 font-semibold">
                  {data.hero.preHeadline}
                </p>
              </div>
            )}

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              {data.hero.headline}
            </h1>

            {data.hero.subheadline && (
              <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                {data.hero.subheadline}
              </p>
            )}

            {/* Hero image — shorter & less wide */}
            <div className="mb-8 max-w-xl mx-auto">
              <img
                src={data.hero.image}
                alt={data.meta.brandName}
                className="w-full rounded-xl shadow-xl object-cover object-top"
                style={{ maxHeight: 280 }}
              />
            </div>

            {/* CTAs */}
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to={data.routes.payment}
                onClick={() => {
                  try {
                    window.fbq?.("trackSingle", PIXEL_ID, "InitiateCheckout", {
                      currency: "NGN",
                      value: 197893,
                    });
                  } catch {}
                }}
                className="inline-flex items-center justify-center bg-brandRed hover:bg-brandRedHover text-white font-bold text-base px-8 py-4 rounded-lg shadow-lg transition-colors"
              >
                BUY NOW [EARLY BIRD]
              </Link>

              <Link
                to={data.routes.demoRequest}
                className="inline-flex items-center justify-center border-2 border-brandRed text-black font-bold hover:border-brandRedHover text-sm px-6 py-4 rounded-lg transition-colors"
              >
                See Full Demo of Product
              </Link>
            </div>

            {/* Trust badges */}
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {data.meta.trustBadges.map((b) => (
                <span
                  key={b}
                  className="inline-flex items-center gap-1.5 bg-gray-50 border border-gray-100 rounded-lg px-3 py-1 text-xs text-gray-700"
                >
                  <span className="text-green-500">✓</span> {b}
                </span>
              ))}
            </div>
          </section>

          {/* ════════════════════════════════════════
    STATS BAR
════════════════════════════════════════ */}
          {data.stats && data.stats.length > 0 && (
            <section className="px-6 sm:px-12 py-8 bg-blue-900">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {data.stats.map((s, i) => (
                  <div key={i} className="text-center">
                    <p className="text-2xl sm:text-3xl font-extrabold text-white">
                      {s.value}
                    </p>
                    <p className="text-blue-200 text-xs sm:text-sm mt-1">
                      {s.label}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ════════════════════════════════════════
              FEATURES
          ════════════════════════════════════════ */}
          <section id="features" className="px-6 sm:px-12 py-12 bg-yellow-50">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 text-center">
              Everything You Need to Capture, Nurture & Close
            </h2>
            <p className="text-center text-gray-600 mb-8 max-w-xl mx-auto">
              One platform. Zero monthly fees. Fully set up for you in 7 days.
            </p>

            <div className="bg-white rounded-2xl p-6 shadow border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {data.features.map((f: Feature) => (
                  <div
                    key={f.title}
                    className="flex items-start gap-3 p-4 rounded-xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-xl">
                      {f.emoji}
                    </div>
                    <div>
                      <p className="text-gray-900 font-semibold text-sm">
                        {f.title}
                      </p>
                      <p className="text-gray-500 text-sm mt-1">
                        {f.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to={data.routes.payment}
                onClick={() => {
                  try {
                    window.fbq?.("trackSingle", PIXEL_ID, "InitiateCheckout", {
                      currency: "NGN",
                      value: 197893,
                    });
                  } catch {}
                }}
                className="inline-flex items-center justify-center bg-brandRed hover:bg-brandRedHover text-white font-bold text-base px-8 py-4 rounded-lg shadow-lg transition-colors"
              >
                BUY NOW [EARLY BIRD]
              </Link>
            </div>
          </section>

          {/* ════════════════════════════════════════
              PLATFORM IMAGES — stacked with heading + subheading + image
          ════════════════════════════════════════ */}
          <section id="dashboard" className="px-6 sm:px-12 py-16 bg-green-50">
            <div className="text-center mb-14">
              <span className="inline-block px-3 py-1 bg-blue-50 text-blue-900 text-xs font-semibold rounded-lg mb-3">
                Platform Walkthrough
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                See the Dashboard in Action
              </h2>
              <p className="text-gray-600 mt-2 max-w-xl mx-auto">
                Every screen you see below is what your team will use daily —
                designed for clarity, built for results.
              </p>
            </div>

            <div className="space-y-16">
              {data.platformImages.map((img: PlatImg, i: number) => (
                <div key={i} className="flex flex-col items-center gap-5">
                  {/* Centered heading + description */}
                  <div className="text-center max-w-xl">
                    <h3 className="text-xl font-bold text-black mb-2">
                      {img.alt}
                    </h3>
                    {(img as any).description && (
                      <p className="text-gray-800 text-sm leading-relaxed">
                        {(img as any).description}
                      </p>
                    )}
                  </div>

                  {/* Slightly smaller image */}
                  <div className="w-full max-w-2xl rounded-2xl overflow-hidden border-2 border-gray-100 shadow-lg">
                    <img
                      src={img.src}
                      alt={img.alt}
                      className="w-full object-cover"
                      style={{ maxHeight: 340, objectPosition: "top" }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>
          {/* ════════════════════════════════════════
    BONUSES
════════════════════════════════════════ */}
          {data.bonuses?.items && data.bonuses.items.length > 0 && (
            <section id="bonuses" className="px-6 sm:px-12 py-14 bg-amber-50">
              <div className="text-center mb-10">
                <span className="inline-block px-3 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded-lg mb-3">
                  🎁 Included Free
                </span>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                  {data.bonuses.sectionTitle}
                </h2>
                <p className="text-gray-600 mt-2 max-w-xl mx-auto">
                  {data.bonuses.description}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
                {data.bonuses.items.map((bonus, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-2xl p-6 shadow border border-amber-100 flex flex-col gap-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-xl">
                        🎁
                      </div>
                      <span className="text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-lg whitespace-nowrap">
                        {bonus.value}
                      </span>
                    </div>
                    <div>
                      <p className="text-gray-900 font-bold text-base">
                        {bonus.title}
                      </p>
                      <p className="text-gray-500 text-sm mt-1 leading-relaxed">
                        {bonus.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ════════════════════════════════════════
              PRICING — 2-card layout
          ════════════════════════════════════════ */}
          <section id="pricing" className="px-6 sm:px-12 py-16 bg-gray-50">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 bg-orange-50 text-orange-700 text-xs font-semibold rounded-lg mb-3">
                ⚡ Limited Slots Available
              </span>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                One Investment. Lifetime Ownership.
              </h2>
              <p className="text-gray-600 mt-2">
                Choose the payment option that works best for your cash flow.
              </p>
            </div>

            {/* What's included — shared between both cards */}
            <div className="mb-8 bg-white rounded-2xl p-6 shadow-sm border border-gray-200 max-w-2xl mx-auto">
              <p className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">
                Everything included in both plans:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {data.features.map((f: Feature) => (
                  <div key={f.title} className="flex items-start gap-2">
                    <CheckCircle />
                    <span className="text-gray-700 text-sm">{f.title}</span>
                  </div>
                ))}
                <div className="flex items-start gap-2">
                  <CheckCircle />
                  <span className="text-gray-700 text-sm font-medium">
                    {data.pricing.guarantee}
                  </span>
                </div>
              </div>
            </div>

            {/* 2 pricing cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {/* ── Card 1: One-Time Payment ── */}
              <div className="relative bg-white rounded-2xl shadow-xl border-2 border-blue-800 overflow-hidden flex flex-col">
                {/* "Best Value" badge */}
                <div className="bg-blue-800 text-white text-xs font-bold text-center py-2 tracking-widest uppercase">
                  Best Value — Save{" "}
                  {fmtMoney(splitTotal - data.pricing.fullPrice * -1)}
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Zap size={18} className="text-blue-800" />
                    <h3 className="text-lg font-bold text-gray-900">
                      Pay Once, Own Forever
                    </h3>
                  </div>
                  <p className="text-gray-500 text-sm mb-6">
                    One payment. Full access. No more to think about.
                  </p>

                  <div className="mb-6">
                    <span className="text-4xl font-extrabold text-gray-900">
                      {fmtMoney(data.pricing.fullPrice)}
                    </span>
                    <span className="text-gray-500 text-sm ml-2">one-time</span>
                  </div>

                  <ul className="space-y-2 mb-8 flex-1">
                    <li className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="text-green-500">✓</span> Single payment
                      today
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="text-green-500">✓</span> Lowest total
                      cost
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="text-green-500">✓</span> Setup begins
                      immediately
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="text-green-500">✓</span> Full 1-year
                      support included
                    </li>
                  </ul>

                  <Link
                    to={data.routes.payment}
                    onClick={() => {
                      try {
                        window.fbq?.(
                          "trackSingle",
                          PIXEL_ID,
                          "InitiateCheckout",
                          {
                            currency: "NGN",
                            value: 197893,
                          },
                        );
                      } catch {}
                    }}
                    className="block w-full text-center bg-blue-800 hover:bg-blue-900 text-white font-bold py-3.5 rounded-xl transition-colors shadow"
                  >
                    Pay {fmtMoney(data.pricing.fullPrice)} Now
                  </Link>
                </div>
              </div>

              {/* ── Card 2: Split Payment ── */}
              <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-200 overflow-hidden flex flex-col">
                <div className="bg-gray-100 text-gray-600 text-xs font-bold text-center py-2 tracking-widest uppercase">
                  Flexible 3-Month Plan
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar size={18} className="text-gray-600" />
                    <h3 className="text-lg font-bold text-gray-900">
                      Spread the Cost
                    </h3>
                  </div>
                  <p className="text-gray-500 text-sm mb-6">
                    Start today, spread the remaining balance over 2 months.
                  </p>

                  <div className="mb-4">
                    <span className="text-4xl font-extrabold text-gray-900">
                      {fmtMoney(split1)}
                    </span>
                    <span className="text-gray-500 text-sm ml-2">
                      due today
                    </span>
                  </div>

                  {/* Payment schedule breakdown */}
                  <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-2">
                    <p className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">
                      Payment Schedule
                    </p>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">Today (1st payment)</span>
                      <span className="font-bold text-gray-900">
                        {fmtMoney(split1)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">
                        Month 2 (2nd payment)
                      </span>
                      <span className="font-bold text-gray-900">
                        {fmtMoney(split2)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">
                        Month 3 (3rd payment)
                      </span>
                      <span className="font-bold text-gray-900">
                        {fmtMoney(split3)}
                      </span>
                    </div>
                    <div className="pt-2 mt-2 border-t border-gray-200 flex justify-between items-center text-sm">
                      <span className="text-gray-700 font-semibold">Total</span>
                      <span className="font-extrabold text-gray-900">
                        {fmtMoney(splitTotal)}
                      </span>
                    </div>
                  </div>

                  <ul className="space-y-2 mb-8 flex-1">
                    <li className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="text-green-500">✓</span> Start with{" "}
                      {fmtMoney(split1)} today
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="text-green-500">✓</span> 2 equal monthly
                      payments after
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="text-green-500">✓</span> Setup begins
                      after 1st payment
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="text-green-500">✓</span> Full 1-year
                      support included
                    </li>
                  </ul>

                  <button
                    onClick={() => {
                      try {
                        window.fbq?.(
                          "trackSingle",
                          PIXEL_ID,
                          "InitiateCheckout",
                          {
                            currency: "NGN",
                            value: 83333,
                          },
                        );
                      } catch {}
                      navigate("pay");
                    }}
                    className="block w-full text-center border-2 border-gray-800 text-gray-800 hover:bg-gray-800 hover:text-white font-bold py-3.5 rounded-xl transition-colors"
                  >
                    Start with {fmtMoney(split1)} Today
                  </button>
                </div>
              </div>
            </div>

            <p className="text-xs text-gray-500 mt-6 text-center">
              🔒 Secure checkout via {data.pricing.paymentProvider} ·{" "}
              {data.pricing.guarantee}
            </p>
          </section>

          {/* ════════════════════════════════════════
    GUARANTEE
════════════════════════════════════════ */}
          {data.guarantee?.description && (
            <section className="px-6 sm:px-12 py-14 bg-white">
              <div className="max-w-2xl mx-auto bg-green-50 border-2 border-green-200 rounded-2xl p-8 sm:p-10 text-center">
                {/* Shield icon */}
                <div className="w-16 h-16 rounded-full bg-green-100 border-2 border-green-300 flex items-center justify-center mx-auto mb-5">
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                    <path
                      d="M14 3L4 7v7c0 5.5 4.3 10.7 10 12 5.7-1.3 10-6.5 10-12V7L14 3z"
                      fill="#bbf7d0"
                      stroke="#16a34a"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9 14l3.5 3.5L19 11"
                      stroke="#16a34a"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>

                <span className="inline-block px-3 py-1 bg-green-100 text-green-800 text-xs font-bold rounded-lg uppercase tracking-wide mb-4">
                  Our Guarantee
                </span>

                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">
                  {data.guarantee.guaranteeType}
                </h3>

                <p className="text-gray-600 leading-relaxed max-w-lg mx-auto">
                  {data.guarantee.description}
                </p>
              </div>
            </section>
          )}

          {/* ════════════════════════════════════════
              FAQ
          ════════════════════════════════════════ */}
          {data.faqs?.questions && data.faqs.questions.length > 0 && (
            <section id="faqs" className="px-6 sm:px-12 py-12 bg-white">
              <h3 className="text-2xl font-bold mb-2 text-center">
                {data.faqs.sectionTitle ?? "FAQ"}
              </h3>
              <p className="text-gray-600 text-center mb-8">
                Still on the fence? These answers might help.
              </p>
              <div className="space-y-3 max-w-3xl mx-auto">
                {data.faqs.questions.map((q: FaqItem, idx: number) => (
                  <div
                    key={idx}
                    className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() => toggleFaq(idx)}
                      className="w-full px-6 py-4 flex justify-between items-center hover:bg-gray-100 transition-colors text-left"
                    >
                      <span className="font-semibold text-gray-900 pr-4">
                        {q.question}
                      </span>
                      {expandedFaq === idx ? (
                        <ChevronUp
                          className="flex-shrink-0 text-gray-500"
                          size={18}
                        />
                      ) : (
                        <ChevronDown
                          className="flex-shrink-0 text-gray-500"
                          size={18}
                        />
                      )}
                    </button>
                    {expandedFaq === idx && (
                      <div className="px-6 py-5 bg-white border-t border-gray-200">
                        <p className="text-gray-700 leading-relaxed">
                          {q.answer}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ════════════════════════════════════════
              FINAL CTA
          ════════════════════════════════════════ */}
          <section className="px-6 sm:px-12 py-14 bg-gradient-to-br from-brandNavy to-blue-900 text-white text-center">
            <h3 className="text-2xl sm:text-3xl font-bold mb-3">
              {data.cta?.buttonText ?? "Secure Your Slot"}
            </h3>
            {data.cta?.secondaryText && (
              <p className="text-blue-200 mb-8 max-w-xl mx-auto">
                {data.cta.secondaryText}
              </p>
            )}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                onClick={() => {
                  try {
                    window.fbq?.("trackSingle", PIXEL_ID, "InitiateCheckout", {
                      currency: "NGN",
                      value: 197893,
                    });
                  } catch {}
                }}
                to={data.cta?.buttonUrl || data.routes.payment}
                className="px-10 py-4 bg-brandRed hover:bg-brandRedHover text-white font-bold rounded-lg shadow-2xl transition-colors text-lg"
              >
                {data.cta?.buttonText ?? "Secure Your Slot"} →
              </Link>
              <a
                href="#pricing"
                className="text-blue-200 hover:text-white text-sm underline underline-offset-4 transition-colors"
              >
                View payment options
              </a>
            </div>

            {data.postScript?.text && (
              <p className="mt-8 text-blue-200 italic max-w-2xl mx-auto text-sm">
                {data.postScript.text}
              </p>
            )}
            {data.postScript?.additionalUrgency && (
              <p className="mt-2 text-blue-300 text-xs">
                {data.postScript.additionalUrgency}
              </p>
            )}
          </section>
        </div>
      </main>

      {/* ── Footer ── */}
      <footer className="py-6 text-center text-sm text-gray-500 bg-white border-t">
        <div className="max-w-4xl mx-auto px-4">
          © {new Date().getFullYear()} {data.meta.brandName}. All rights
          reserved.
          {data.settings?.showPoweredBy && (
            <>
              {" "}
              ·{" "}
              <a
                className="underline hover:text-gray-700"
                href={data.settings.companyUrl}
                target="_blank"
                rel="noreferrer"
              >
                Privacy / Terms
              </a>
            </>
          )}
        </div>
      </footer>
    </div>
  );
};

export default CustomFunnelSalesPage;
